import { useNavigate } from "react-router-dom";
import { Server, Clock, HardDrive, CheckCircle, AlertTriangle, XCircle, Loader2, HelpCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const statusConfig = {
  success: {
    color: "border-l-emerald-500",
    bgColor: "bg-emerald-500/10",
    icon: CheckCircle,
    iconColor: "text-emerald-500",
    label: "Succes"
  },
  warning: {
    color: "border-l-amber-500",
    bgColor: "bg-amber-500/10",
    icon: AlertTriangle,
    iconColor: "text-amber-500",
    label: "Attention"
  },
  error: {
    color: "border-l-red-500",
    bgColor: "bg-red-500/10",
    icon: XCircle,
    iconColor: "text-red-500",
    label: "Erreur"
  },
  running: {
    color: "border-l-blue-500",
    bgColor: "bg-blue-500/10",
    icon: Loader2,
    iconColor: "text-blue-500 animate-spin",
    label: "En cours"
  },
  unknown: {
    color: "border-l-slate-500",
    bgColor: "bg-slate-500/10",
    icon: HelpCircle,
    iconColor: "text-slate-500",
    label: "Inconnu"
  }
};

const agentTypeConfig = {
  veeam: { icon: Server, color: "text-green-400", label: "Veeam" },
  iperius: { icon: HardDrive, color: "text-orange-400", label: "Iperius" }
};

const formatBytes = (bytes) => {
  if (!bytes) return "-";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const ServerCard = ({ server, showClientName = false }) => {
  const navigate = useNavigate();
  const status = statusConfig[server.last_backup_status] || statusConfig.unknown;
  const StatusIcon = status.icon;
  const agentType = agentTypeConfig[server.agent_type] || agentTypeConfig.veeam;
  const AgentIcon = agentType.icon;

  const lastBackupTime = server.last_backup_time
    ? formatDistanceToNow(new Date(server.last_backup_time), { addSuffix: true, locale: fr })
    : "Jamais";

  return (
    <div
      onClick={() => navigate(`/servers/${server.id}`)}
      className={`glass-card rounded-lg border-l-4 ${status.color} p-4 cursor-pointer hover:bg-slate-800/50 transition-all duration-200 ${
        server.last_backup_status === "error" ? "pulse-error" : ""
      }`}
      data-testid={`server-card-${server.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-md ${status.bgColor}`}>
            <AgentIcon className={agentType.color} size={18} />
          </div>
          <div>
            <h3 className="font-mono font-medium text-white text-sm truncate max-w-[140px]">
              {server.hostname}
            </h3>
            {showClientName && server.client_name && (
              <p className="text-xs text-slate-500">{server.client_name}</p>
            )}
          </div>
        </div>
        <StatusIcon className={status.iconColor} size={20} />
      </div>

      {/* Agent type badge */}
      <div className="mb-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${server.agent_type === "iperius" ? "bg-orange-500/20 text-orange-400" : "bg-green-500/20 text-green-400"}`}>
          {agentType.label}
        </span>
      </div>

      {/* Info */}
      <div className="space-y-2">
        {server.last_backup_job_name && (
          <p className="text-xs text-slate-400 truncate">
            Job: <span className="font-mono text-slate-300">{server.last_backup_job_name}</span>
          </p>
        )}
        
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <Clock size={12} />
            <span>{lastBackupTime}</span>
          </div>
          {server.last_backup_size && (
            <div className="flex items-center gap-1">
              <HardDrive size={12} />
              <span className="font-mono">{formatBytes(server.last_backup_size)}</span>
            </div>
          )}
        </div>

        {server.last_backup_message && (
          <p className="text-xs text-slate-500 truncate" title={server.last_backup_message}>
            {server.last_backup_message}
          </p>
        )}

        {server.ip_address && (
          <p className="text-xs font-mono text-slate-600">{server.ip_address}</p>
        )}
      </div>
    </div>
  );
};

export default ServerCard;
